import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  CloudSync as HubSpotIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface Integration {
  id: string;
  provider: string;
  status: string; // 'connected', 'disconnected', 'error'
  last_sync?: string;
  account_name?: string;
  error_message?: string;
}

interface IntegrationConfig {
  name: string;
  description: string;
  icon: React.ReactNode;
  provider: string;
  oauth: boolean;
}

const integrationConfigs: IntegrationConfig[] = [
  {
    name: 'LinkedIn',
    description: 'Post content and engage with your LinkedIn audience',
    icon: <LinkedInIcon fontSize="large" color="primary" />,
    provider: 'linkedin',
    oauth: true,
  },
  {
    name: 'Twitter (X)',
    description: 'Share tweets and monitor mentions',
    icon: <TwitterIcon fontSize="large" sx={{ color: '#1DA1F2' }} />,
    provider: 'twitter',
    oauth: true,
  },
  {
    name: 'Facebook',
    description: 'Manage your Facebook page and posts',
    icon: <FacebookIcon fontSize="large" sx={{ color: '#1877F2' }} />,
    provider: 'facebook',
    oauth: true,
  },
  {
    name: 'HubSpot',
    description: 'Sync contacts, leads, and campaign data',
    icon: <HubSpotIcon fontSize="large" sx={{ color: '#FF7A59' }} />,
    provider: 'hubspot',
    oauth: true,
  },
  {
    name: 'SendGrid',
    description: 'Send email campaigns and track deliverability',
    icon: <EmailIcon fontSize="large" sx={{ color: '#1A82E2' }} />,
    provider: 'sendgrid',
    oauth: false,
  },
];

export default function Integrations() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/integrations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setIntegrations(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider: string, oauth: boolean) => {
    try {
      if (oauth) {
        // Initiate OAuth flow
        const response = await axios.post(
          `${API_URL}/integrations/${provider}/connect`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        // Redirect to OAuth URL
        if (response.data.oauth_url) {
          window.location.href = response.data.oauth_url;
        }
      } else {
        // For non-OAuth integrations, API keys should be configured in Settings
        setError(`Please configure ${provider} API keys in Settings > API Keys`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to connect integration');
    }
  };

  const handleDisconnect = async (provider: string) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider}?`)) return;

    try {
      await axios.delete(`${API_URL}/integrations/${provider}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadIntegrations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to disconnect integration');
    }
  };

  const handleSync = async (provider: string) => {
    try {
      await axios.post(
        `${API_URL}/integrations/${provider}/sync`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      loadIntegrations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to sync integration');
    }
  };

  const getIntegrationStatus = (provider: string): Integration | undefined => {
    return integrations.find(i => i.provider === provider);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your marketing tools to automate workflows
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {integrationConfigs.map((config) => {
          const status = getIntegrationStatus(config.provider);
          const isConnected = status?.status === 'connected';

          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={config.provider}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    {config.icon}
                    {isConnected && (
                      <Chip label="Connected" color="success" size="small" />
                    )}
                    {status?.status === 'error' && (
                      <Chip label="Error" color="error" size="small" />
                    )}
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {config.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {config.description}
                  </Typography>

                  {isConnected && status && (
                    <Box sx={{ mb: 2 }}>
                      <List dense>
                        {status.account_name && (
                          <ListItem disablePadding>
                            <ListItemText
                              primary={
                                <Typography variant="caption" color="text.secondary">
                                  Account
                                </Typography>
                              }
                              secondary={status.account_name}
                            />
                          </ListItem>
                        )}
                        {status.last_sync && (
                          <ListItem disablePadding>
                            <ListItemText
                              primary={
                                <Typography variant="caption" color="text.secondary">
                                  Last Synced
                                </Typography>
                              }
                              secondary={new Date(status.last_sync).toLocaleString()}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}

                  {status?.error_message && (
                    <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }}>
                      {status.error_message}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isConnected ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => handleSync(config.provider)}
                          fullWidth
                        >
                          Sync Now
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDisconnect(config.provider)}
                        >
                          <LinkOffIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LinkIcon />}
                        onClick={() => handleConnect(config.provider, config.oauth)}
                        fullWidth
                      >
                        Connect
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Integration Tips
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="OAuth Integrations"
              secondary="LinkedIn, Twitter, Facebook, and HubSpot use OAuth for secure connection. You'll be redirected to authenticate."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="API Key Integrations"
              secondary="For SendGrid and other services, configure API keys in Settings > API Keys before connecting."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Auto-Sync"
              secondary="Connected integrations sync automatically every hour. Use 'Sync Now' for immediate updates."
            />
          </ListItem>
        </List>
      </Box>
    </Box>
  );
}
